/* ============================================================
   MODULO — REVEAL POPUP
   Two-step email capture. One controller per [data-modulo-prv]
   section. Center modal only (scroll-lock + focus-trap). The
   gate reveals the email field in-place; a collapsed pane is
   made `inert` so Tab and assistive tech skip it. Adapted from
   the Signup Popup controller (.modulo-psu).
   ============================================================ */
(function () {
  "use strict";
  if (typeof window === "undefined" || typeof document === "undefined") return;

  var REDUCED = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var DESIGN = !!(window.Shopify && window.Shopify.designMode);
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  /* ---- body scroll-lock, instance-counted, gutter-compensated (CLS-safe) ---- */
  var lockCount = 0, savedY = 0;
  function lockScroll() {
    if (lockCount++ === 0) {
      savedY = window.scrollY || window.pageYOffset || 0;
      var sbw = window.innerWidth - document.documentElement.clientWidth;
      if (sbw > 0) document.body.style.paddingRight = sbw + "px";
      document.body.style.top = -savedY + "px";
      document.body.classList.add("modulo-prv--locked");
    }
  }
  function unlockScroll() {
    if (lockCount > 0 && --lockCount === 0) {
      document.body.classList.remove("modulo-prv--locked");
      document.body.style.top = "";
      document.body.style.paddingRight = "";
      window.scrollTo(0, savedY);
    }
  }

  /* set/clear inertness on a collapsed pane. We set the property (native
     skipping where supported) AND reflect the attribute so the manual
     focus-trap filter below stays correct in every browser. */
  function setInert(el, on) {
    if (!el) return;
    el.inert = on;
    if (on) el.setAttribute("inert", ""); else el.removeAttribute("inert");
  }

  function initSection(sectionEl) {
    if (sectionEl._prvInit) return;
    sectionEl._prvInit = true;

    var scrim = sectionEl.querySelector("[data-prv-scrim]");
    if (!scrim) return;
    var dialog = scrim.querySelector("[data-prv-dialog]");
    var closeBtn = scrim.querySelector("[data-prv-close]");
    var launcher = sectionEl.querySelector("[data-prv-launcher]");
    var gate = scrim.querySelector("[data-prv-gate]");
    var formPane = scrim.querySelector("[data-prv-formpane]");
    var yesBtn = scrim.querySelector("[data-prv-yes]");
    var declineBtn = scrim.querySelector("[data-prv-decline]");
    var backBtn = scrim.querySelector("[data-prv-back]");
    var form = scrim.querySelector(".modulo-prv__form");
    var email = scrim.querySelector("[data-prv-email]");
    var errEl = scrim.querySelector("[data-prv-err]");
    var doneEl = scrim.querySelector("[data-prv-done]");
    var submitBtn = scrim.querySelector("[data-prv-submit]");
    var honeypot = scrim.querySelector("[data-prv-hp]");

    var isOpen = false, lastFocus = null;

    /* ---- step machine: 'gate' | 'form' | 'done' ---- */
    function setStep(step) {
      sectionEl.classList.toggle("is-step2", step === "form");
      sectionEl.classList.toggle("is-done", step === "done");
      setInert(gate, step !== "gate");
      setInert(formPane, step !== "form");
    }
    setInert(formPane, true); // gate is the initial step

    /* ---- copy button ---- */
    var copyBtn = scrim.querySelector("[data-prv-copy]");
    var copyLbl = scrim.querySelector("[data-prv-copylabel]");
    var copyText = copyLbl ? copyLbl.textContent : "Copy";
    if (copyBtn) copyBtn.addEventListener("click", function () {
      var code = copyBtn.getAttribute("data-code") || "";
      var notify = function () {
        copyBtn.classList.add("is-copied");
        if (copyLbl) copyLbl.textContent = "Copied";
        setTimeout(function () { copyBtn.classList.remove("is-copied"); if (copyLbl) copyLbl.textContent = copyText; }, 1900);
      };
      function fallback() {
        var t = document.createElement("textarea");
        t.value = code; t.style.position = "fixed"; t.style.opacity = "0";
        document.body.appendChild(t); t.select();
        try { document.execCommand("copy"); } catch (_) {}
        t.remove(); notify();
      }
      if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(code).then(notify, fallback);
      else fallback();
    });

    function focusables() {
      return Array.prototype.slice.call(dialog.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )).filter(function (el) {
        return el.offsetParent !== null && !el.closest("[inert]");
      });
    }

    /* ---- open / close ---- */
    function open() {
      if (isOpen) return;
      isOpen = true;
      lastFocus = document.activeElement;
      scrim.hidden = false;
      scrim.classList.remove("is-closing");
      lockScroll();
      if (launcher) launcher.classList.remove("show");
      dialog.style.animation = "none"; void dialog.offsetWidth; dialog.style.animation = "";
      // focus the close (NOT the email) so mobile keyboards never shove the sheet
      requestAnimationFrame(function () {
        if (closeBtn) try { closeBtn.focus({ preventScroll: true }); } catch (_) { closeBtn.focus(); }
      });
    }

    function close() {
      if (!isOpen) return;
      isOpen = false;
      unlockScroll();
      var finishClose = function () {
        scrim.hidden = true;
        scrim.classList.remove("is-closing");
        if (launcher) launcher.classList.add("show");
        if (lastFocus && lastFocus.focus) try { lastFocus.focus({ preventScroll: true }); } catch (_) {}
      };
      if (REDUCED) { finishClose(); return; }
      scrim.classList.add("is-closing");
      var fired = false;
      var fin = function () { if (fired) return; fired = true; scrim.removeEventListener("transitionend", fin); finishClose(); };
      scrim.addEventListener("transitionend", fin);
      setTimeout(fin, 460);
    }

    function release() { if (isOpen) { isOpen = false; unlockScroll(); } }
    sectionEl._prvRelease = release;

    /* ---- step transitions ---- */
    if (yesBtn) yesBtn.addEventListener("click", function () {
      setStep("form");
      requestAnimationFrame(function () {
        if (email) try { email.focus({ preventScroll: true }); } catch (_) { email.focus(); }
      });
    });
    if (backBtn) backBtn.addEventListener("click", function () {
      setStep("gate");
      requestAnimationFrame(function () {
        if (yesBtn) try { yesBtn.focus({ preventScroll: true }); } catch (_) { yesBtn.focus(); }
      });
    });
    if (declineBtn) declineBtn.addEventListener("click", close);
    if (closeBtn) closeBtn.addEventListener("click", close);

    /* scrim click closes (modal) — guard against drag-release outside */
    var downOnScrim = false;
    scrim.addEventListener("mousedown", function (e) { downOnScrim = e.target === scrim; });
    scrim.addEventListener("mouseup", function (e) { if (downOnScrim && e.target === scrim) close(); downOnScrim = false; });

    /* ESC + focus trap */
    scrim.addEventListener("keydown", function (e) {
      if (scrim.hidden) return;
      if (e.key === "Escape") { e.preventDefault(); close(); return; }
      if (e.key === "Tab") {
        var f = focusables(); if (!f.length) return;
        var first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    });

    /* ---- form: validate + AJAX + success ---- */
    function showError() { if (errEl) errEl.classList.add("show"); if (email) email.setAttribute("aria-invalid", "true"); }
    function clearError() { if (email) email.removeAttribute("aria-invalid"); if (errEl) errEl.classList.remove("show"); }
    function settle() {
      setStep("done");
      if (doneEl) doneEl.classList.add("show");
      if (submitBtn) { submitBtn.removeAttribute("aria-busy"); submitBtn.disabled = true; }
      var target = copyBtn || closeBtn;
      if (target) setTimeout(function () { try { target.focus({ preventScroll: true }); } catch (_) { target.focus(); } }, 140);
    }

    if (form) form.addEventListener("submit", function (e) {
      if (form.dataset.prvDone === "1") { e.preventDefault(); return; }
      if (honeypot && honeypot.value) { e.preventDefault(); return; }
      var v = (email && email.value || "").trim();
      if (!v || !EMAIL_RE.test(v)) { e.preventDefault(); showError(); if (email) email.focus(); return; }
      clearError();
      // no fetch/FormData → let the native POST + reload reveal the success state
      if (!window.fetch || !window.FormData) { form.dataset.prvDone = "1"; return; }
      e.preventDefault();
      form.dataset.prvDone = "1";
      if (submitBtn) submitBtn.setAttribute("aria-busy", "true");
      var action = form.getAttribute("action") || window.location.pathname;
      var doneOnce = false;
      var finish = function () { if (doneOnce) return; doneOnce = true; if (REDUCED) settle(); else setTimeout(settle, 300); };
      // a rejected server response (4xx/5xx) must NOT render a false "you're in" —
      // fall back to the native POST so the real Shopify result / error shows.
      var native = function () { form.dataset.prvDone = ""; form.submit(); };
      fetch(action, { method: "POST", body: new FormData(form), headers: { "Accept": "text/html" }, credentials: "same-origin" })
        .then(function (r) { if (r && r.ok === false) { native(); return; } finish(); }, native);
    });

    if (email) email.addEventListener("input", function () {
      if (email.getAttribute("aria-invalid") === "true" && EMAIL_RE.test(email.value.trim())) clearError();
    });

    if (launcher) launcher.addEventListener("click", open);

    /* ---- triggers + frequency cap ---- */
    function storeKey() { return "modulo-prv-" + (sectionEl.getAttribute("data-section-id") || "x"); }
    function suppressed() {
      var days = parseInt(sectionEl.getAttribute("data-frequency"), 10) || 0;
      if (days <= 0) return false;
      try {
        var ts = parseInt(window.localStorage.getItem(storeKey()), 10);
        if (!ts) return false;
        return (Date.now() - ts) < days * 86400000;
      } catch (_) { return false; }
    }
    function stamp() { try { window.localStorage.setItem(storeKey(), String(Date.now())); } catch (_) {} }

    var opened = false;
    function openOnce() { if (opened) return; opened = true; stamp(); open(); }

    // returning from a no-fetch native submit: reopen straight to the success state
    if (scrim.querySelector("[data-prv-posted]")) {
      if (form) form.dataset.prvDone = "1";
      setStep("done");
      if (doneEl) doneEl.classList.add("show");
      if (submitBtn) submitBtn.disabled = true;
      openOnce();
      return;
    }

    // theme editor: always preview, ignore the frequency cap
    if (DESIGN) { open(); if (launcher) launcher.classList.add("show"); return; }
    if (suppressed()) { if (launcher) launcher.classList.add("show"); return; }

    var trigger = sectionEl.getAttribute("data-trigger") || "delay";
    if (trigger === "button") { if (launcher) launcher.classList.add("show"); return; }
    if (trigger === "page_load") { openOnce(); return; }
    if (trigger === "delay") {
      var secs = parseInt(sectionEl.getAttribute("data-delay"), 10); if (isNaN(secs)) secs = 4;
      setTimeout(openOnce, secs * 1000); if (launcher) launcher.classList.add("show"); return;
    }
    if (trigger === "scroll") {
      var pct = parseInt(sectionEl.getAttribute("data-scroll"), 10) || 35;
      var onScroll = function () {
        var docH = document.documentElement.scrollHeight - window.innerHeight;
        var reached = docH <= 0 ? 100 : ((window.scrollY || window.pageYOffset) / docH) * 100;
        if (reached >= pct) { window.removeEventListener("scroll", onScroll); openOnce(); }
      };
      window.addEventListener("scroll", onScroll, { passive: true });
      if (launcher) launcher.classList.add("show"); return;
    }
    if (trigger === "exit") {
      var onOut = function (e) {
        if (e.clientY <= 0 && !e.relatedTarget && !e.toElement) { document.removeEventListener("mouseout", onOut); openOnce(); }
      };
      document.addEventListener("mouseout", onOut);
      if (launcher) launcher.classList.add("show"); return;
    }
    if (launcher) launcher.classList.add("show");
  }

  function boot() {
    var els = document.querySelectorAll("[data-modulo-prv]");
    for (var i = 0; i < els.length; i++) initSection(els[i]);
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();

  // theme editor: re-init on add/edit, release a torn-down section's scroll-lock
  if (DESIGN) {
    document.addEventListener("shopify:section:load", function (e) {
      var el = e.target && e.target.querySelector ? e.target.querySelector("[data-modulo-prv]") : null;
      if (el) initSection(el);
    });
    document.addEventListener("shopify:section:unload", function (e) {
      var el = e.target && e.target.querySelector ? e.target.querySelector("[data-modulo-prv]") : null;
      if (el && el._prvRelease) el._prvRelease();
    });
  }
})();
